/**
 * CampaignWizard — ویزارد ۴ مرحله‌ای ساخت/ویرایش کمپین قرعه‌کشی سراسری
 *
 * مراحل: ۱) اطلاعات کلی  ۲) شرط‌های شرکت  ۳) جوایز  ۴) تنظیمات و بازبینی
 * صرفا UI — بدنهٔ درخواست POST/PUT /admin/lottery/campaigns دقیقا مثل فرم قبلی
 * ساخته می‌شود (buildPayload بدون تغییر منتقل شده است).
 */

import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TuneIcon from '@mui/icons-material/Tune';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded';
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded';
import ShoppingBagRoundedIcon from '@mui/icons-material/ShoppingBagRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import TodayRoundedIcon from '@mui/icons-material/TodayRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import DateRangeRoundedIcon from '@mui/icons-material/DateRangeRounded';
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded';
import { useSnackbar } from 'notistack';
import ResponsiveModal from '../common/ResponsiveModal';
import JalaliDatePicker, { toLatinDigits, toPersianDigits } from '../common/JalaliDatePicker';
import { formatDate } from '../../utils/dateUtils';
import lotteryService, {
  CampaignInput,
  LotteryAggregate,
  LotteryCampaignItem,
  LotteryEntryMode,
  LotteryMilestone,
  LotteryMilestoneEvent,
  LotteryPrize,
  LotteryPrizeType,
  PRIZE_TYPE_LABELS,
} from '../../services/lottery.service';

// ---------------------------------------------------------------------------
// Brand palette (visual identity shared with the public site)
// ---------------------------------------------------------------------------

const NAVY = '#101f4e';
const NAVY_2 = '#1b2f77';
const GOLD_CTA = 'linear-gradient(135deg,#ffc107,#f5a300)';
const GREEN = '#2e7d32';
const GREEN_TINT = '#f4faf4';
const GREEN_BORDER = '#c8e6c9';
const BLUE = '#0066ff';
const BLUE_TINT = '#eef3ff';
const CARD_BORDER = '#ececec';
const INNER_BG = '#fbfbfc';
const INNER_BORDER = '#f0f0f0';

// ---------------------------------------------------------------------------
// Form types & defaults (state shape identical to the old inline form,
// plus a UI-only `titleTouched` flag that never reaches the payload)
// ---------------------------------------------------------------------------

interface MilestoneForm {
  title: string;
  event: LotteryMilestoneEvent;
  aggregate: LotteryAggregate;
  target: string;
  countUnit: 'order' | 'item';
  minAmount: string;
  repeatable: boolean;
  maxRepeats: string;
  manualInstructions: string;
  /** UI-only: وقتی ادمین عنوان را دستی عوض کرد، دیگر خودکار بازنویسی نمی‌شود */
  titleTouched: boolean;
}

interface PrizeForm {
  title: string;
  type: LotteryPrizeType;
  amount: string;
  description: string;
}

interface CampaignForm {
  title: string;
  description: string;
  imageUrl: string;
  startAt: string; // ISO
  endAt: string; // ISO
  entryMode: LotteryEntryMode;
  milestones: MilestoneForm[];
  prizes: PrizeForm[];
  requirePhoneVerified: boolean;
  minAccountAgeDays: string;
  activate: boolean;
}

const emptyPrize = (): PrizeForm => ({
  title: '',
  type: 'wallet_credit',
  amount: '',
  description: '',
});

const emptyForm = (): CampaignForm => ({
  title: '',
  description: '',
  imageUrl: '',
  startAt: '',
  endAt: '',
  entryMode: 'all_milestones',
  milestones: [],
  prizes: [emptyPrize()],
  requirePhoneVerified: true,
  minAccountAgeDays: '',
  activate: false,
});

const campaignToForm = (c: LotteryCampaignItem): CampaignForm => ({
  title: c.title || '',
  description: c.description || '',
  imageUrl: c.imageUrl || '',
  startAt: c.startAt || '',
  endAt: c.endAt || '',
  entryMode: c.entryMode || 'all_milestones',
  milestones: (c.milestones || []).map((m: LotteryMilestone) => ({
    title: m.title || '',
    event: m.event,
    aggregate: m.aggregate || 'count',
    target: String(m.target ?? 1),
    countUnit: m.countUnit || 'order',
    minAmount: m.filters?.minAmount ? String(m.filters.minAmount) : '',
    repeatable: !!m.repeatable,
    maxRepeats: m.maxRepeats ? String(m.maxRepeats) : '',
    manualInstructions: m.manualInstructions || '',
    titleTouched: true,
  })),
  prizes: (c.prizes || []).map((p: LotteryPrize) => ({
    title: p.title || '',
    type: p.type,
    amount: p.amount ? String(p.amount) : '',
    description: p.description || '',
  })),
  requirePhoneVerified: c.eligibility?.requirePhoneVerified !== false,
  minAccountAgeDays: c.eligibility?.minAccountAgeDays ? String(c.eligibility.minAccountAgeDays) : '',
  activate: false,
});

// ---------------------------------------------------------------------------
// Milestone event metadata (chooser cards + summaries)
// ---------------------------------------------------------------------------

interface EventMeta {
  icon: React.ReactElement;
  title: string;
  desc: string;
  /** پسوند فیلد هدف، مثل «بار» یا «روز» */
  unit: string;
  defaultTarget: string;
  /** رنگ پس‌زمینهٔ کاشی آیکون */
  tint: string;
  /** رنگ خود آیکون */
  color: string;
}

const EVENT_META: Record<LotteryMilestoneEvent, EventMeta> = {
  purchase: {
    icon: <ShoppingCartRoundedIcon />,
    title: 'خرید',
    desc: 'خرید از سایت یا یک فروشگاه',
    unit: 'بار',
    defaultTarget: '1',
    tint: '#eef3ff',
    color: '#0066ff',
  },
  review: {
    icon: <RateReviewRoundedIcon />,
    title: 'دیدگاه',
    desc: 'ثبت دیدگاه تاییدشده روی محصولات',
    unit: 'دیدگاه',
    defaultTarget: '1',
    tint: '#f0e9ff',
    color: '#7b3ff2',
  },
  referral_visit: {
    icon: <LinkRoundedIcon />,
    title: 'دعوت — بازدید',
    desc: 'بازدید دوستان از لینک دعوت کاربر',
    unit: 'بازدید',
    defaultTarget: '10',
    tint: '#e6f7f1',
    color: '#0f9d6b',
  },
  referral_signup: {
    icon: <GroupAddRoundedIcon />,
    title: 'دعوت — ثبت‌نام',
    desc: 'ثبت‌نام دوستان با لینک دعوت',
    unit: 'ثبت‌نام',
    defaultTarget: '3',
    tint: '#e6f7f1',
    color: '#0f9d6b',
  },
  referral_purchase: {
    icon: <ShoppingBagRoundedIcon />,
    title: 'دعوت — خرید',
    desc: 'خرید توسط دوستان دعوت‌شده',
    unit: 'خرید',
    defaultTarget: '1',
    tint: '#e6f7f1',
    color: '#0f9d6b',
  },
  wishlist: {
    icon: <FavoriteRoundedIcon />,
    title: 'علاقه‌مندی',
    desc: 'افزودن محصولات به لیست علاقه‌مندی',
    unit: 'محصول',
    defaultTarget: '3',
    tint: '#ffeaee',
    color: '#e0325a',
  },
  daily_checkin: {
    icon: <TodayRoundedIcon />,
    title: 'حضور روزانه',
    desc: 'ورود روزانه به سایت یا اپلیکیشن',
    unit: 'روز',
    defaultTarget: '7',
    tint: '#fff4e0',
    color: '#e08700',
  },
  manual: {
    icon: <TaskAltRoundedIcon />,
    title: 'شرط دلخواه با مدرک',
    desc: 'هر شرطی؛ کاربر مدرک می‌فرستد و شما تایید می‌کنید',
    unit: 'بار',
    defaultTarget: '1',
    tint: '#eef2f6',
    color: '#48627d',
  },
};

const EVENT_ORDER: LotteryMilestoneEvent[] = [
  'purchase',
  'review',
  'referral_visit',
  'referral_signup',
  'referral_purchase',
  'wishlist',
  'daily_checkin',
  'manual',
];

const PRIZE_RANK_WORDS = ['اول', 'دوم', 'سوم', 'چهارم', 'پنجم', 'ششم', 'هفتم', 'هشتم', 'نهم', 'دهم'];

const STEPS = ['اطلاعات کلی', 'شرط‌های شرکت', 'جوایز', 'تنظیمات و بازبینی'];

// ---------------------------------------------------------------------------
// Number helpers — نمایش با جداکنندهٔ هزارگان، ذخیره به‌صورت رقم خام
// ---------------------------------------------------------------------------

const stripDigits = (s: string): string => toLatinDigits(s).replace(/[^\d]/g, '');
const withSep = (s: string): string => (s ? Number(s).toLocaleString('en-US') : '');
const faNum = (v: string | number): string =>
  toPersianDigits(Number(v || 0).toLocaleString('en-US'));

/** عنوان خودکار شرط بر اساس نوع/هدف — مثل «۳ بار خرید» یا «مجموع ۵۰۰,۰۰۰ تومان خرید» */
const autoSummary = (m: Pick<MilestoneForm, 'event' | 'aggregate' | 'target' | 'countUnit'>): string => {
  const t = faNum(m.target || '0');
  switch (m.event) {
    case 'purchase':
      if (m.aggregate === 'sum_amount') return `مجموع ${t} تومان خرید`;
      return m.countUnit === 'item' ? `خرید ${t} آیتم` : `${t} بار خرید`;
    case 'review':
      return `${t} دیدگاه تاییدشده`;
    case 'referral_visit':
      return `${t} بازدید از لینک دعوت`;
    case 'referral_signup':
      return `${t} ثبت‌نام با لینک دعوت`;
    case 'referral_purchase':
      return `${t} خرید توسط دعوت‌شده‌ها`;
    case 'wishlist':
      return `افزودن ${t} محصول به علاقه‌مندی‌ها`;
    case 'daily_checkin':
      return `${t} روز حضور روزانه`;
    default:
      return '';
  }
};

// ---------------------------------------------------------------------------
// Small presentational bits
// ---------------------------------------------------------------------------

/** کارت قابل‌انتخاب (نوع شرط / شیوهٔ شانس‌دهی) با هاور و حالت انتخاب */
const SelectableCard: React.FC<{
  selected?: boolean;
  onClick: () => void;
  children: React.ReactNode;
  sx?: any;
}> = ({ selected = false, onClick, children, sx }) => (
  <Paper
    elevation={0}
    onClick={onClick}
    sx={{
      p: 1.75,
      borderRadius: 2.5,
      cursor: 'pointer',
      border: '1px solid',
      transition: 'border-color .15s, background-color .15s, transform .15s',
      borderColor: selected ? BLUE : CARD_BORDER,
      bgcolor: selected ? BLUE_TINT : 'background.paper',
      ...(selected ? { boxShadow: `0 0 0 1px ${BLUE} inset` } : {}),
      '&:hover': {
        borderColor: BLUE,
        transform: 'translateY(-2px)',
      },
      ...sx,
    }}
  >
    {children}
  </Paper>
);

const SectionCaption: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, color: NAVY, opacity: 0.85 }}>
    {children}
  </Typography>
);

/** کاشی آیکون رنگی هر نوع شرط (۴۲×۴۲ با گوشهٔ ۱۲px) */
const EventIconTile: React.FC<{ event: LotteryMilestoneEvent; size?: number }> = ({ event, size = 42 }) => {
  const meta = EVENT_META[event];
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '12px',
        bgcolor: meta.tint,
        color: meta.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        '& svg': { fontSize: Math.round(size * 0.52) },
      }}
    >
      {meta.icon}
    </Box>
  );
};

/** کاشی آیکون عمومی (ردیف‌های بازبینی مرحلهٔ ۴) */
const SummaryIconTile: React.FC<{ icon: React.ReactElement; tint: string; color: string }> = ({
  icon,
  tint,
  color,
}) => (
  <Box
    sx={{
      width: 34,
      height: 34,
      borderRadius: '10px',
      bgcolor: tint,
      color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      '& svg': { fontSize: 18 },
    }}
  >
    {icon}
  </Box>
);

/** مدال رتبهٔ جایزه — طلا / نقره / برنز و خاکستری برای بقیه */
const PRIZE_RANK_STYLES: { background: string; color: string }[] = [
  { background: 'linear-gradient(135deg,#ffd54f,#f5a300)', color: '#5b4300' },
  { background: 'linear-gradient(135deg,#e8eaf0,#b9c0cc)', color: '#3d4657' },
  { background: 'linear-gradient(135deg,#ffb98a,#d97a3a)', color: '#5d2f0e' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface CampaignWizardProps {
  open: boolean;
  /** null = ساخت کمپین جدید */
  campaign: LotteryCampaignItem | null;
  onClose: () => void;
  /** بعد از ذخیرهٔ موفق صدا زده می‌شود (والد لیست را refresh و مودال را می‌بندد) */
  onSaved: () => void;
}

const CampaignWizard: React.FC<CampaignWizardProps> = ({ open, campaign, onClose, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const editing = !!campaign;

  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState<CampaignForm>(emptyForm());
  const [saving, setSaving] = useState(false);
  /** بعد از اولین تلاش ناموفق «بعدی»، خطاهای inline مرحلهٔ جاری نمایش داده می‌شوند */
  const [showErrors, setShowErrors] = useState(false);
  /** ایندکس شرطِ باز (ادیتور) در مرحلهٔ ۲ */
  const [expandedMilestone, setExpandedMilestone] = useState<number | null>(null);
  /** نمایش گرید انتخاب نوع شرط وقتی حداقل یک شرط داریم */
  const [chooserOpen, setChooserOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(campaign ? campaignToForm(campaign) : emptyForm());
      setActiveStep(0);
      setShowErrors(false);
      setExpandedMilestone(null);
      setChooserOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ------------------------------------------------------------- mutations

  const patchMilestone = (index: number, patch: Partial<MilestoneForm>) => {
    setForm((f) => ({
      ...f,
      milestones: f.milestones.map((m, i) => {
        if (i !== index) return m;
        const next = { ...m, ...patch };
        if (!next.titleTouched && next.event !== 'manual') next.title = autoSummary(next);
        return next;
      }),
    }));
  };

  const addMilestone = (event: LotteryMilestoneEvent) => {
    const meta = EVENT_META[event];
    const base: MilestoneForm = {
      title: '',
      event,
      aggregate: 'count',
      target: meta.defaultTarget,
      countUnit: 'order',
      minAmount: '',
      repeatable: false,
      maxRepeats: '',
      manualInstructions: '',
      titleTouched: false,
    };
    if (event !== 'manual') base.title = autoSummary(base);
    setExpandedMilestone(form.milestones.length);
    setForm((f) => ({ ...f, milestones: [...f.milestones, base] }));
    setChooserOpen(false);
  };

  const removeMilestone = (index: number) => {
    setForm((f) => ({ ...f, milestones: f.milestones.filter((_m, i) => i !== index) }));
    setExpandedMilestone((cur) => {
      if (cur === null) return null;
      if (cur === index) return null;
      return cur > index ? cur - 1 : cur;
    });
  };

  const setPrize = (index: number, patch: Partial<PrizeForm>) => {
    setForm((f) => ({
      ...f,
      prizes: f.prizes.map((p, i) => (i === index ? { ...p, ...patch } : p)),
    }));
  };

  // ------------------------------------------------------------ validation

  const validateStep = (step: number): string | null => {
    if (step === 0) {
      if (!form.title.trim()) return 'عنوان کمپین الزامی است';
      if (!form.startAt) return 'تاریخ شروع را انتخاب کنید';
      if (!form.endAt) return 'تاریخ پایان را انتخاب کنید';
      if (new Date(form.endAt).getTime() <= new Date(form.startAt).getTime())
        return 'تاریخ پایان باید بعد از تاریخ شروع باشد';
    }
    if (step === 1) {
      if (!form.milestones.length) return 'حداقل یک شرط شرکت تعریف کنید';
      for (let i = 0; i < form.milestones.length; i++) {
        const m = form.milestones[i];
        if (!m.title.trim()) return `عنوان شرط ${toPersianDigits(String(i + 1))} الزامی است`;
        if (!(Number(m.target) > 0))
          return `هدف شرط ${toPersianDigits(String(i + 1))} باید بزرگتر از صفر باشد`;
        if (m.event === 'manual' && !m.manualInstructions.trim())
          return `برای شرط ${toPersianDigits(String(i + 1))} بنویسید کاربر چه مدرکی بفرستد`;
      }
    }
    if (step === 2) {
      if (!form.prizes.length) return 'حداقل یک جایزه تعریف کنید';
      for (let i = 0; i < form.prizes.length; i++) {
        const p = form.prizes[i];
        if (!p.title.trim()) return `عنوان جایزه ${toPersianDigits(String(i + 1))} الزامی است`;
        if (p.type === 'wallet_credit' && !(Number(p.amount) > 0))
          return `مبلغ جایزه ${toPersianDigits(String(i + 1))} (شارژ کیف پول) الزامی است`;
      }
    }
    return null;
  };

  const goNext = () => {
    const err = validateStep(activeStep);
    if (err) {
      setShowErrors(true);
      enqueueSnackbar(err, { variant: 'warning' });
      return;
    }
    setShowErrors(false);
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const goBack = () => {
    setShowErrors(false);
    setActiveStep((s) => Math.max(s - 1, 0));
  };

  const jumpTo = (step: number) => {
    if (step < activeStep) {
      setShowErrors(false);
      setActiveStep(step);
    }
  };

  // ---------------------------------------------------------------- submit
  // buildPayload: بدون تغییر از فرم قبلی — خروجی باید دقیقا همان بدنهٔ قبلی باشد

  const buildPayload = (): CampaignInput => ({
    title: form.title.trim(),
    description: form.description.trim() || undefined,
    imageUrl: form.imageUrl.trim() || undefined,
    startAt: form.startAt,
    endAt: form.endAt,
    entryMode: form.entryMode,
    milestones: form.milestones.map((m) => ({
      title: m.title.trim(),
      event: m.event,
      aggregate: m.event === 'purchase' ? m.aggregate : 'count',
      target: Number(m.target),
      ...(m.event === 'purchase' && m.aggregate === 'count' ? { countUnit: m.countUnit } : {}),
      ...(m.event === 'purchase' && Number(m.minAmount) > 0
        ? { filters: { minAmount: Number(m.minAmount) } }
        : {}),
      repeatable: m.repeatable,
      ...(m.repeatable && Number(m.maxRepeats) > 0 ? { maxRepeats: Number(m.maxRepeats) } : {}),
      ...(m.event === 'manual' ? { manualInstructions: m.manualInstructions.trim() } : {}),
    })),
    prizes: form.prizes.map((p) => ({
      title: p.title.trim(),
      type: p.type,
      ...(p.type === 'wallet_credit' ? { amount: Number(p.amount) } : {}),
      ...(p.description.trim() ? { description: p.description.trim() } : {}),
    })),
    eligibility: {
      requirePhoneVerified: form.requirePhoneVerified,
      ...(Number(form.minAccountAgeDays) > 0 ? { minAccountAgeDays: Number(form.minAccountAgeDays) } : {}),
    },
    ...(editing ? {} : { activate: form.activate }),
  });

  const submit = async () => {
    // safety: همهٔ مراحل یک‌بار دیگر چک می‌شوند
    for (let s = 0; s <= 2; s++) {
      const err = validateStep(s);
      if (err) {
        setActiveStep(s);
        setShowErrors(true);
        enqueueSnackbar(err, { variant: 'warning' });
        return;
      }
    }
    setSaving(true);
    try {
      if (campaign) {
        await lotteryService.updateCampaign(campaign._id, buildPayload());
        enqueueSnackbar('کمپین با موفقیت ویرایش شد', { variant: 'success' });
      } else {
        await lotteryService.createCampaign(buildPayload());
        enqueueSnackbar(
          form.activate ? 'کمپین ساخته و بلافاصله فعال شد' : 'کمپین به‌صورت پیش‌نویس ساخته شد',
          { variant: 'success' }
        );
      }
      onSaved();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'خطا در ذخیره کمپین', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------- step 1

  const renderStepGeneral = () => {
    const datesInvalid =
      !!form.startAt &&
      !!form.endAt &&
      new Date(form.endAt).getTime() <= new Date(form.startAt).getTime();
    return (
      <Stack spacing={3}>
        <Stack spacing={0.75}>
          <SectionCaption>مشخصات کمپین</SectionCaption>
          <TextField
            label="عنوان کمپین *"
            fullWidth
            value={form.title}
            error={showErrors && !form.title.trim()}
            helperText={showErrors && !form.title.trim() ? 'عنوان کمپین الزامی است' : undefined}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          />
        </Stack>
        <TextField
          label="توضیحات"
          fullWidth
          multiline
          minRows={3}
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
        />
        <TextField
          label="آدرس تصویر (اختیاری)"
          size="small"
          fullWidth
          dir="ltr"
          placeholder="https://..."
          value={form.imageUrl}
          onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))}
        />
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SummaryIconTile icon={<DateRangeRoundedIcon />} tint={BLUE_TINT} color={BLUE} />
            <SectionCaption>بازهٔ برگزاری</SectionCaption>
          </Box>
          <Box
            sx={{
              p: 2,
              borderRadius: 2.5,
              bgcolor: INNER_BG,
              border: `1px solid ${INNER_BORDER}`,
            }}
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  تاریخ شروع *
                </Typography>
                <JalaliDatePicker
                  value={form.startAt}
                  onChange={(iso) => setForm((f) => ({ ...f, startAt: iso }))}
                  placeholder="انتخاب تاریخ شروع"
                />
                {showErrors && !form.startAt && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                    تاریخ شروع را انتخاب کنید
                  </Typography>
                )}
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  تاریخ پایان *
                </Typography>
                <JalaliDatePicker
                  value={form.endAt}
                  onChange={(iso) => setForm((f) => ({ ...f, endAt: iso }))}
                  placeholder="انتخاب تاریخ پایان"
                />
                {showErrors && !form.endAt && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                    تاریخ پایان را انتخاب کنید
                  </Typography>
                )}
              </Box>
            </Box>
            {datesInvalid && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                تاریخ پایان باید بعد از تاریخ شروع باشد
              </Typography>
            )}
          </Box>
        </Stack>
      </Stack>
    );
  };

  // ---------------------------------------------------------------- step 2

  const renderEventChooser = () => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
        gap: 1.5,
      }}
    >
      {EVENT_ORDER.map((ev) => {
        const meta = EVENT_META[ev];
        return (
          <SelectableCard
            key={ev}
            onClick={() => addMilestone(ev)}
            sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
          >
            <Box sx={{ mb: 1 }}>
              <EventIconTile event={ev} />
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.25, color: NAVY }}>
              {meta.title}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.6 }}>
              {meta.desc}
            </Typography>
          </SelectableCard>
        );
      })}
    </Box>
  );

  /** «تنظیمات بیشتر» — آکاردئون جمع‌شده داخل ادیتور هر شرط */
  const renderAdvanced = (m: MilestoneForm, i: number) => (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        border: `1px solid ${INNER_BORDER}`,
        borderRadius: '10px !important',
        '&::before': { display: 'none' },
        bgcolor: INNER_BG,
        boxShadow: 'none',
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TuneIcon fontSize="small" color="action" />
          <Typography variant="body2" color="text.secondary">
            تنظیمات بیشتر
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          {m.event === 'purchase' && (
            <TextField
              label="حداقل مبلغ هر خرید (اختیاری)"
              size="small"
              dir="ltr"
              value={withSep(m.minAmount)}
              onChange={(e) => patchMilestone(i, { minAmount: stripDigits(e.target.value) })}
              InputProps={{ endAdornment: <InputAdornment position="end">تومان</InputAdornment> }}
              helperText="خریدهای کمتر از این مبلغ شمرده نمی‌شوند"
            />
          )}
          {m.event === 'purchase' && m.aggregate === 'count' && (
            <TextField
              select
              label="واحد شمارش"
              size="small"
              value={m.countUnit}
              onChange={(e) => patchMilestone(i, { countUnit: e.target.value as 'order' | 'item' })}
              helperText="هر سفارش یک بار شمرده شود یا هر آیتم داخل سفارش"
            >
              <MenuItem value="order">سفارش</MenuItem>
              <MenuItem value="item">آیتم</MenuItem>
            </TextField>
          )}
          {m.event === 'manual' && (
            <TextField
              label="هدف (چند بار تایید مدرک)"
              size="small"
              dir="ltr"
              value={m.target}
              error={showErrors && !(Number(m.target) > 0)}
              onChange={(e) => patchMilestone(i, { target: stripDigits(e.target.value) })}
              InputProps={{ endAdornment: <InputAdornment position="end">بار</InputAdornment> }}
            />
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={m.repeatable}
                  onChange={(e) => patchMilestone(i, { repeatable: e.target.checked })}
                />
              }
              label={<Typography variant="body2">تکرارپذیر (تکمیل دوباره = شانس بیشتر)</Typography>}
            />
            {m.repeatable && (
              <TextField
                label="حداکثر تکرار"
                size="small"
                dir="ltr"
                value={m.maxRepeats}
                onChange={(e) => patchMilestone(i, { maxRepeats: stripDigits(e.target.value) })}
                sx={{ maxWidth: 150 }}
              />
            )}
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );

  const renderMilestoneEditor = (m: MilestoneForm, i: number) => {
    const isSum = m.event === 'purchase' && m.aggregate === 'sum_amount';
    const targetError = showErrors && !(Number(m.target) > 0);
    return (
      <Stack spacing={2.5} sx={{ p: 2, pt: 1.5 }}>
        {m.event === 'purchase' && (
          <Stack spacing={0.75}>
            <SectionCaption>سنجهٔ خرید</SectionCaption>
            <ToggleButtonGroup
              exclusive
              size="small"
              color="primary"
              value={m.aggregate}
              onChange={(_e, v) => v && patchMilestone(i, { aggregate: v as LotteryAggregate })}
            >
              <ToggleButton value="count" sx={{ px: 2 }}>
                تعداد خرید
              </ToggleButton>
              <ToggleButton value="sum_amount" sx={{ px: 2 }}>
                مجموع مبلغ
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        )}

        {m.event === 'manual' ? (
          <>
            <TextField
              label="عنوان شرط *"
              size="small"
              fullWidth
              value={m.title}
              error={showErrors && !m.title.trim()}
              helperText={
                showErrors && !m.title.trim()
                  ? 'عنوان شرط الزامی است'
                  : 'مثلا: «استوری کمپین را در اینستاگرام بازنشر کن»'
              }
              onChange={(e) => patchMilestone(i, { title: e.target.value, titleTouched: true })}
            />
            <TextField
              label="کاربر چه مدرکی بفرستد؟ *"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={m.manualInstructions}
              error={showErrors && !m.manualInstructions.trim()}
              helperText="به کاربر بگویید چه مدرکی (تصویر/توضیح) باید ارسال کند تا شما تایید کنید"
              onChange={(e) => patchMilestone(i, { manualInstructions: e.target.value })}
            />
          </>
        ) : (
          <>
            <TextField
              label={isSum ? 'هدف (مجموع مبلغ) *' : 'هدف *'}
              size="small"
              dir="ltr"
              value={isSum ? withSep(m.target) : m.target}
              error={targetError}
              helperText={targetError ? 'هدف باید بزرگتر از صفر باشد' : undefined}
              onChange={(e) => patchMilestone(i, { target: stripDigits(e.target.value) })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {isSum ? 'تومان' : EVENT_META[m.event].unit}
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 280 }}
            />
            <TextField
              label="عنوان شرط"
              size="small"
              fullWidth
              value={m.title}
              error={showErrors && !m.title.trim()}
              helperText="به همین شکل به کاربر نمایش داده می‌شود — می‌توانید عنوان پیشنهادی را نگه دارید"
              onChange={(e) =>
                patchMilestone(i, { title: e.target.value, titleTouched: e.target.value.trim().length > 0 })
              }
              onBlur={() => {
                if (!m.title.trim()) patchMilestone(i, { titleTouched: false });
              }}
            />
          </>
        )}

        {renderAdvanced(m, i)}
      </Stack>
    );
  };

  const renderMilestoneCard = (m: MilestoneForm, i: number) => {
    const meta = EVENT_META[m.event];
    const expanded = expandedMilestone === i;
    const summary = m.title.trim() || autoSummary(m) || meta.title;
    return (
      <Paper
        key={i}
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: expanded ? BLUE : CARD_BORDER,
          transition: 'border-color .15s',
        }}
      >
        <Box
          onClick={() => setExpandedMilestone(expanded ? null : i)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.5,
            cursor: 'pointer',
            '&:hover': { bgcolor: INNER_BG },
          }}
        >
          <EventIconTile event={m.event} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: NAVY }} noWrap>
              {summary}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {meta.title}
            </Typography>
          </Box>
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 0.75, flexShrink: 0 }}>
            <Chip
              size="small"
              label={`هدف: ${faNum(m.target || '0')}${
                m.event === 'purchase' && m.aggregate === 'sum_amount' ? ' تومان' : ''
              }`}
              sx={{ bgcolor: meta.tint, color: meta.color, fontWeight: 700, border: 'none' }}
            />
            {m.repeatable && (
              <Chip
                size="small"
                label="تکرارپذیر"
                sx={{ bgcolor: GREEN_TINT, color: GREEN, fontWeight: 700, border: `1px solid ${GREEN_BORDER}` }}
              />
            )}
          </Box>
          <Tooltip title={expanded ? 'بستن' : 'ویرایش'} arrow>
            <IconButton size="small">
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="حذف شرط" arrow>
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                removeMilestone(i);
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Collapse in={expanded} unmountOnExit>
          <Divider sx={{ borderColor: INNER_BORDER }} />
          {renderMilestoneEditor(m, i)}
        </Collapse>
      </Paper>
    );
  };

  const renderStepMilestones = () => (
    <Stack spacing={2.5}>
      {form.milestones.length === 0 ? (
        <>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                mx: 'auto',
                mb: 1.5,
                borderRadius: '16px',
                bgcolor: BLUE_TINT,
                color: BLUE,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& svg': { fontSize: 30 },
              }}
            >
              <ChecklistRoundedIcon />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: NAVY }}>
              کاربران برای شرکت در قرعه‌کشی چه کاری انجام دهند؟
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              نوع شرط را از کارت‌های زیر انتخاب کنید — می‌توانید چند شرط اضافه کنید.
            </Typography>
          </Box>
          {renderEventChooser()}
          {showErrors && (
            <Alert severity="warning">حداقل یک شرط برای شرکت در قرعه‌کشی اضافه کنید.</Alert>
          )}
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <SectionCaption>
              شرط‌های شرکت ({toPersianDigits(String(form.milestones.length))})
            </SectionCaption>
            <Button
              size="small"
              startIcon={<AddIcon />}
              variant={chooserOpen ? 'contained' : 'outlined'}
              onClick={() => setChooserOpen((v) => !v)}
              sx={
                chooserOpen
                  ? { bgcolor: NAVY, boxShadow: 'none', '&:hover': { bgcolor: NAVY_2, boxShadow: 'none' } }
                  : { color: NAVY, borderColor: NAVY, '&:hover': { borderColor: NAVY_2, bgcolor: BLUE_TINT } }
              }
            >
              افزودن شرط
            </Button>
          </Box>
          <Collapse in={chooserOpen} unmountOnExit>
            <Paper
              elevation={0}
              sx={{ p: 2, borderRadius: 2.5, bgcolor: INNER_BG, border: `1px solid ${INNER_BORDER}` }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                نوع شرط جدید را انتخاب کنید:
              </Typography>
              {renderEventChooser()}
            </Paper>
          </Collapse>
          <Stack spacing={1.5}>{form.milestones.map((m, i) => renderMilestoneCard(m, i))}</Stack>
        </>
      )}
    </Stack>
  );

  // ---------------------------------------------------------------- step 3

  const renderStepPrizes = () => (
    <Stack spacing={2.5}>
      <Box
        sx={{
          p: 1.75,
          borderRadius: 2.5,
          background: 'linear-gradient(135deg,#fff8e2,#ffedb8)',
          border: '1px solid #ffe08a',
        }}
      >
        <Typography variant="body2" sx={{ color: '#5b4300', fontWeight: 700 }}>
          ترتیب جوایز = رتبهٔ برنده‌ها؛ جایزهٔ اول به برندهٔ اول می‌رسد.
        </Typography>
      </Box>
      <Stack spacing={1.5}>
        {form.prizes.map((p, i) => {
          const rankWord = PRIZE_RANK_WORDS[i] || toPersianDigits(String(i + 1));
          const medal = PRIZE_RANK_STYLES[i] || { background: '#f2f2f4', color: '#6b7280' };
          return (
            <Paper
              key={i}
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: `1px solid ${CARD_BORDER}`,
                ...(i === 0 ? { bgcolor: '#fffdf5' } : {}),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: '50%',
                      background: medal.background,
                      color: medal.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      fontSize: 15,
                      flexShrink: 0,
                    }}
                  >
                    {toPersianDigits(String(i + 1))}
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: NAVY }}>
                    جایزهٔ {toPersianDigits(String(i + 1))} (رتبه {rankWord})
                  </Typography>
                </Box>
                <Tooltip title="حذف جایزه" arrow>
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      disabled={form.prizes.length <= 1}
                      onClick={() =>
                        setForm((f) => ({ ...f, prizes: f.prizes.filter((_x, xi) => xi !== i) }))
                      }
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              <Stack spacing={2}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 1fr' }, gap: 2 }}>
                  <TextField
                    label="عنوان جایزه *"
                    size="small"
                    fullWidth
                    value={p.title}
                    error={showErrors && !p.title.trim()}
                    helperText={showErrors && !p.title.trim() ? 'عنوان جایزه الزامی است' : undefined}
                    onChange={(e) => setPrize(i, { title: e.target.value })}
                  />
                  <TextField
                    select
                    label="نوع جایزه"
                    size="small"
                    value={p.type}
                    onChange={(e) => setPrize(i, { type: e.target.value as LotteryPrizeType })}
                  >
                    {(Object.keys(PRIZE_TYPE_LABELS) as LotteryPrizeType[]).map((t) => (
                      <MenuItem key={t} value={t}>
                        {PRIZE_TYPE_LABELS[t]}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                {p.type === 'wallet_credit' && (
                  <TextField
                    label="مبلغ *"
                    size="small"
                    dir="ltr"
                    value={withSep(p.amount)}
                    error={showErrors && !(Number(p.amount) > 0)}
                    helperText={
                      showErrors && !(Number(p.amount) > 0)
                        ? 'مبلغ شارژ کیف پول الزامی است'
                        : undefined
                    }
                    onChange={(e) => setPrize(i, { amount: stripDigits(e.target.value) })}
                    InputProps={{ endAdornment: <InputAdornment position="end">تومان</InputAdornment> }}
                    sx={{ maxWidth: 280 }}
                  />
                )}
                <TextField
                  label="توضیح (اختیاری)"
                  size="small"
                  fullWidth
                  value={p.description}
                  onChange={(e) => setPrize(i, { description: e.target.value })}
                />
              </Stack>
            </Paper>
          );
        })}
      </Stack>
      <Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          variant="outlined"
          onClick={() => setForm((f) => ({ ...f, prizes: [...f.prizes, emptyPrize()] }))}
          sx={{ color: NAVY, borderColor: NAVY, '&:hover': { borderColor: NAVY_2, bgcolor: BLUE_TINT } }}
        >
          افزودن جایزه
        </Button>
      </Box>
    </Stack>
  );

  // ---------------------------------------------------------------- step 4

  const ENTRY_MODE_CARDS: { value: LotteryEntryMode; title: string; desc: string }[] = [
    {
      value: 'all_milestones',
      title: 'تکمیل همهٔ شرط‌ها = یک شانس',
      desc: 'کاربر با تکمیل همهٔ شرط‌ها یک شانس می‌گیرد؛ تکرار شرط‌های تکرارپذیر بعد از تکمیل، شانس اضافه می‌دهد.',
    },
    {
      value: 'ticket_per_milestone',
      title: 'هر شرط = یک شانس مستقل',
      desc: 'هر بار تکمیل هر شرط، یک شانس جداگانه در قرعه‌کشی می‌دهد.',
    },
  ];

  const renderStepSettings = () => {
    const usedEvents = Array.from(new Set(form.milestones.map((m) => m.event)));
    return (
      <Stack spacing={3}>
        <Stack spacing={1}>
          <SectionCaption>شیوهٔ شانس‌دهی</SectionCaption>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            {ENTRY_MODE_CARDS.map((card) => (
              <SelectableCard
                key={card.value}
                selected={form.entryMode === card.value}
                onClick={() => setForm((f) => ({ ...f, entryMode: card.value }))}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                  <SummaryIconTile
                    icon={<ConfirmationNumberRoundedIcon />}
                    tint={form.entryMode === card.value ? '#dbe8ff' : '#eef2f6'}
                    color={form.entryMode === card.value ? BLUE : '#48627d'}
                  />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: NAVY }}>
                      {card.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.5, lineHeight: 1.7 }}
                    >
                      {card.desc}
                    </Typography>
                  </Box>
                </Box>
              </SelectableCard>
            ))}
          </Box>
        </Stack>

        <Stack spacing={1.5}>
          <SectionCaption>شرایط شرکت‌کنندگان</SectionCaption>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={form.requirePhoneVerified}
                onChange={(e) => setForm((f) => ({ ...f, requirePhoneVerified: e.target.checked }))}
              />
            }
            label={<Typography variant="body2">فقط کاربران با موبایل تاییدشده</Typography>}
          />
          <TextField
            label="حداقل قدمت حساب (روز — اختیاری)"
            size="small"
            dir="ltr"
            value={form.minAccountAgeDays}
            onChange={(e) => setForm((f) => ({ ...f, minAccountAgeDays: stripDigits(e.target.value) }))}
            sx={{ maxWidth: 280 }}
          />
        </Stack>

        {!editing && (
          <Stack spacing={1}>
            <SectionCaption>انتشار</SectionCaption>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderRadius: 2.5,
                background: 'linear-gradient(135deg,#fff8e2,#ffedb8)',
                border: '1px solid #ffe08a',
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={form.activate}
                    onChange={(e) => setForm((f) => ({ ...f, activate: e.target.checked }))}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: '#5b4300', fontWeight: 700 }}>
                    فعال‌سازی بلافاصله
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{ display: 'block', color: '#8a6100', fontWeight: 400 }}
                    >
                      در غیر این صورت کمپین به‌صورت پیش‌نویس ذخیره می‌شود.
                    </Typography>
                  </Typography>
                }
              />
            </Box>
          </Stack>
        )}

        <Stack spacing={1}>
          <SectionCaption>بازبینی نهایی</SectionCaption>
          <Paper
            elevation={0}
            sx={{ p: 2, borderRadius: 2.5, bgcolor: INNER_BG, border: `1px solid ${INNER_BORDER}` }}
          >
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <SummaryIconTile icon={<DateRangeRoundedIcon />} tint={BLUE_TINT} color={BLUE} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {form.startAt && form.endAt
                    ? `از ${formatDate(form.startAt)} تا ${formatDate(form.endAt)}`
                    : 'بازهٔ برگزاری انتخاب نشده'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                <SummaryIconTile icon={<ChecklistRoundedIcon />} tint="#eef2f6" color="#48627d" />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {toPersianDigits(String(form.milestones.length))} شرط شرکت
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, '& svg': { fontSize: 18 } }}>
                  {usedEvents.map((ev) => (
                    <Tooltip key={ev} title={EVENT_META[ev].title} arrow>
                      <Box sx={{ display: 'flex', color: EVENT_META[ev].color }}>{EVENT_META[ev].icon}</Box>
                    </Tooltip>
                  ))}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <SummaryIconTile icon={<EmojiEventsRoundedIcon />} tint="#fff8e2" color="#8a6100" />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {toPersianDigits(String(form.prizes.length))} جایزه
                  {form.prizes[0]?.title.trim() ? ` — جایزهٔ اول: ${form.prizes[0].title.trim()}` : ''}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <SummaryIconTile icon={<ConfirmationNumberRoundedIcon />} tint="#f0e9ff" color="#7b3ff2" />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {ENTRY_MODE_CARDS.find((c) => c.value === form.entryMode)?.title}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Stack>
      </Stack>
    );
  };

  // ----------------------------------------------------------------- render

  const stepContent = [renderStepGeneral, renderStepMilestones, renderStepPrizes, renderStepSettings];

  return (
    <ResponsiveModal
      open={open}
      onClose={() => !saving && onClose()}
      title={editing ? 'ویرایش کمپین قرعه‌کشی' : 'کمپین قرعه‌کشی جدید (سراسری)'}
      subtitle={editing ? campaign?.title : 'کمپین سراسری سایت — کمپین فروشگاهی را فروشنده می‌سازد'}
      maxWidth="md"
      mobileBreakpoint="md"
      disableClose={saving}
      id="lottery-campaign-wizard"
      actions={
        <>
          <Button color="inherit" onClick={onClose} disabled={saving} sx={{ color: 'text.secondary' }}>
            انصراف
          </Button>
          {activeStep > 0 && (
            <Button
              variant="outlined"
              color="inherit"
              onClick={goBack}
              disabled={saving}
              sx={{ borderColor: CARD_BORDER, color: 'text.secondary' }}
            >
              قبلی
            </Button>
          )}
          {activeStep < STEPS.length - 1 ? (
            <Button
              variant="contained"
              onClick={goNext}
              sx={{
                bgcolor: NAVY,
                boxShadow: 'none',
                px: 3,
                fontWeight: 700,
                '&:hover': { bgcolor: NAVY_2, boxShadow: 'none' },
              }}
            >
              مرحلهٔ بعد
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={submit}
              disabled={saving}
              sx={{
                background: GOLD_CTA,
                color: NAVY,
                fontWeight: 800,
                px: 3,
                boxShadow: '0 4px 14px rgba(255,193,7,0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg,#f5b800,#e09600)',
                  boxShadow: '0 4px 14px rgba(255,193,7,0.5)',
                },
                '&.Mui-disabled': { background: '#f0f0f0', color: 'text.disabled', boxShadow: 'none' },
              }}
            >
              {editing ? 'ذخیره تغییرات' : form.activate ? 'ایجاد و فعال‌سازی' : 'ایجاد کمپین'}
            </Button>
          )}
        </>
      }
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3.5, px: 0.5 }}>
        {STEPS.map((label, idx) => {
          const completed = idx < activeStep;
          const active = idx === activeStep;
          return (
            <React.Fragment key={label}>
              {idx > 0 && (
                <Box
                  sx={{
                    flex: 1,
                    height: 2,
                    mx: 1,
                    borderRadius: 1,
                    bgcolor: idx <= activeStep ? GREEN : CARD_BORDER,
                    minWidth: 12,
                  }}
                />
              )}
              <Box
                onClick={() => jumpTo(idx)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  flexShrink: 0,
                  cursor: completed ? 'pointer' : 'default',
                }}
              >
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 800,
                    flexShrink: 0,
                    ...(completed
                      ? { bgcolor: GREEN_TINT, color: GREEN, border: `1px solid ${GREEN_BORDER}` }
                      : active
                      ? { bgcolor: NAVY, color: '#fff' }
                      : { bgcolor: '#f2f2f4', color: '#9aa0ac' }),
                  }}
                >
                  {completed ? <CheckRoundedIcon sx={{ fontSize: 15 }} /> : toPersianDigits(String(idx + 1))}
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: active ? 800 : 600,
                    color: active ? NAVY : completed ? GREEN : 'text.disabled',
                    display: { xs: active ? 'block' : 'none', sm: 'block' },
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </Typography>
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
      {stepContent[activeStep]()}
    </ResponsiveModal>
  );
};

export default CampaignWizard;
